<template>
  <div
    v-show="isVisible"
    class="ml-2"
  >
    <ConfirmDialog group="autoGenerateOptions" />
    <Dialog
      v-model:visible="showLabelFieldModal"
      :header="t('templateBuilder.formOptions.selectLabelField')"
      :modal="true"
      :closable="true"
      :style="{ width: '30vw' }"
    >
      <div class="flex flex-column gap-3">
        <p>{{ t('templateBuilder.formOptions.selectLabelFieldMessage') }}</p>
        <Dropdown
          v-model="selectedLabelField"
          :options="availableLabelFields"
          option-label="label"
          option-value="value"
          class="w-full"
          :placeholder="t('templateBuilder.formOptions.selectLabelFieldPlaceholder')"
        />
      </div>
      <template #footer>
        <Button
          :label="t('no')"
          icon="pi pi-times"
          class="p-button-text"
          @click="handleLabelFieldReject"
        />
        <Button
          :label="t('yes')"
          icon="pi pi-check"
          :disabled="!selectedLabelField"
          @click="handleLabelFieldAccept"
        />
      </template>
    </Dialog>
    <div class="flex justify-content-between flex-wrap">
      <div class="flex justify-content-center">
        <label
          class="block my-3"
          data-test-id="dropdown-label"
        >
          {{ t(configs.label, null) }}
        </label>
      </div>
    </div>
    <div
      id="form-properties-wrapper"
      class="flex flex-column gap-3"
    >
      <div
        v-for="(optionLabel, idx) in optionLabels"
        :key="optionIds[idx]"
        class="flex border-1 border-200 border-round pr-3 pt-2"
        :class="{ 'pb-4': optionExpanded[idx], 'pb-2': !optionExpanded[idx] }"
      >
        <div class="handle-drag flex align-items-center px-3"><i class="fa-solid fa-grip-dots-vertical" /></div>
        <div
          class="flex flex-column flex-grow-1"
          :class="{ 'gap-2': optionExpanded[idx] }"
        >
          <div class="flex align-items-center">
            <div class="flex gap-1 align-items-center">
              <SpmButton
                :icon="optionExpanded[idx] ? 'fa-regular fa-chevron-up' : 'fa-regular fa-chevron-down'"
                class-style="p-button p-button-secondary p-button-text"
                @click="toggleExpanded(idx)"
              />
              <span class="mr-auto font-semibold">{{ t('templateBuilder.formProperty') }}</span>
            </div>
            <SpmButton
              class="ml-auto"
              icon="fa-regular fa-trash"
              class-style="p-button p-button-secondary"
              @click="deleteOption(idx)"
            />
          </div>
          <div
            class="form-field-options__wrapper flex flex-column gap-1"
            :class="{ 'form-field-options__wrapper--collapsed': !optionExpanded[idx] }"
          >
            <div class="flex flex-column gap-1">
              <label>{{ t('templateBuilder.formPropertyLabel') }}</label>
              <BaseInputText
                v-model="optionLabels[idx]"
                :display-emojis="true"
                :display-variables="true"
                :variables-list="enrichedVariables"
                class="w-full"
                @update:model-value="handleOptionChange"
              />
            </div>
            <div class="flex flex-column gap-1">
              <label>{{ t('templateBuilder.formPropertyValue') }}</label>
              <Dropdown
                v-model="optionValues[idx]"
                class="w-full p-0"
                :options="getAvailableOptions(idx)"
                option-label="label"
                option-value="value"
                option-disabled="disabled"
                filter
                reset-filter-on-hide
                @filter="handleFilter"
                @change="handleOptionChange"
              >
                <template #empty>
                  <div class="flex flex column">
                    <span class="p-2">
                      {{ t('templateBuilder.formOptionCreateValue') }}
                    </span>
                  </div>
                </template>
                <template #emptyfilter>
                  <div class="flex flex-column">
                    <span class="p-2">
                      {{ t('templateBuilder.formOptionEmptySearch', [filterValue]) }}
                    </span>
                    <span
                      v-if="!restrictAddOption"
                      class="p-2 cursor-pointer"
                      @click="() => handleCreateOption(idx)"
                    >
                      {{ t('templateBuilder.formOptionCreateEntry', [filterValue]) }}
                    </span>
                  </div>
                </template>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
    </div>
    <SpmButton
      :label="t('templateBuilder.formOptionAdd')"
      icon="fa-regular fa-plus"
      class-style="p-button p-button-secondary"
      class="mt-3"
      :disabled="restrictAddOption && optionLabels.length >= optionsList.length"
      @click="() => addOption(`Option ${optionLabels.length + 1}`)"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  PropType,
  Ref,
  ref,
  computed,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  FieldConfig, FieldsGroup, ParserFieldObject, Property,
} from '@/types';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import variables from '@/components/template-builder/utils/variables-list';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';

import Dropdown from 'primevue/dropdown';
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { useConfirm } from 'primevue/useconfirm';
import { createFormOptionId } from '@/components/template-builder/utils/helpers';
import getNativeContactProperties from '@/configs/native-contact-property';
import { getCustomContactPropertiesWithoutNested } from '@/configs/custom-contact-properties';
import { fetchCustomSchemas } from '@/composables/custom-data/customData';
import { nestCall, NestApiType } from '@/composables/nestApi';

import Sortable from 'sortablejs';
import { getVariablesWithCollectionContext } from '@/composables/template-builder/collectionContext';

export default defineComponent({
  name: 'FormFieldOptions',

  components: {
    BaseInputText,
    Dropdown,
    ConfirmDialog,
    Dialog,
    Button,
    SpmButton,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    fieldGroup: {
      type: Object as PropType<FieldsGroup>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, context) {
    const { t } = useI18n();
    const confirm = useConfirm();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties);
    const optionLabels = ref(
      props.parserValues.properties[0].value.map((opt: any) => opt.optionLabel),
    );
    const optionValues = ref<string[]>(
      props.parserValues.properties[0].value.map((opt: any) => opt.optionValue),
    );
    const optionIds = ref<string[]>(
      props.parserValues.properties[0].value.map((opt: any) => opt.optionId),
    );
    const optionExpanded = ref<boolean[]>(
      props.parserValues.properties[0].value.map(() => true),
    );
    const variableList = ref(variables.allVariables);
    const enrichedVariables = ref(variables.allVariables);
    const optionsList = ref<{ label: string; value: string }[]>(Array.from(new Set(optionValues.value)).filter((el) => el.trim()).map((el) => ({ label: el, value: el })));
    const filterValue = ref('');
    const contactProperty = computed(() => props.fieldGroup.items?.find((item) => item.fieldId === props.configs.parentFieldId));
    const nativeContactProperties = ref(getNativeContactProperties(t, '').map((item) => item.value));
    const restrictAddOption = ref(false);
    const isVisible = ref(false);
    const fullCustomSchemas = ref<any[]>([]);

    // For label field selection modal
    const showLabelFieldModal = ref(false);
    const availableLabelFields = ref<{ label: string; value: string }[]>([]);
    const selectedLabelField = ref<string>('');
    const currentRelationshipData = ref<any>(null);

    const handleOptionChange = () => {
      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = { ...prop };
        updatedProperty.value = optionLabels.value.map((optionLabel: string, index: number) => ({
          optionId: optionIds.value[index],
          optionLabel,
          optionValue: optionValues.value[index],
        }));
        return updatedProperty;
      });
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    const handleFilter = (e: any) => {
      filterValue.value = e.value;
    };

    const handleCreateOption = (index: number) => {
      optionsList.value.push({
        value: filterValue.value,
        label: filterValue.value,
      });
      optionValues.value[index] = filterValue.value;
      handleOptionChange();
    };

    const addOption = (label = '', value = '', emitChange = true) => {
      let newLabel = label;
      let newValue = value;
      // Only use optionsList if no label/value provided (user clicking "Add" button)
      if (restrictAddOption.value && !label && !value) {
        const newOption = optionsList.value.find((option) => !optionValues.value.includes(option.value));
        if (newOption) {
          newLabel = newOption.label;
          newValue = newOption.value;
        }
      }
      optionLabels.value.push(newLabel);
      optionValues.value.push(newValue);

      const filteredOptionIds = optionIds.value.map((el) => {
        const matchedId = el.match(/-(\d+)$/);
        return matchedId ? parseInt(matchedId[1], 10) : 1;
      });
      const maxId = Math.max(...filteredOptionIds);

      const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(props.parserValues.selector);
      if (elementId && elementId.length) {
        const optionId = createFormOptionId(elementId[1], `${maxId + 1}`, props.parserValues.properties[0].type || '');
        optionIds.value.push(optionId);
      }
      optionExpanded.value.push(true);
      if (emitChange) {
        handleOptionChange();
      }
    };

    const deleteOption = (index: number, emitChange = true) => {
      optionLabels.value.splice(index, 1);
      optionValues.value.splice(index, 1);
      optionIds.value.splice(index, 1);
      optionExpanded.value.splice(index, 1);
      if (emitChange) {
        handleOptionChange();
      }
    };

    const clearOptions = () => {
      optionLabels.value = [];
      optionValues.value = [];
      optionIds.value = [];
      optionExpanded.value = [];
    };

    // Get available options for a specific dropdown, marking already used values as disabled
    const getAvailableOptions = (currentIndex: number) => optionsList.value.map((option) => {
      const currentValue = optionValues.value[currentIndex];
      const isUsedByOther = optionValues.value.some((val, idx) => idx !== currentIndex && val === option.value);

      return {
        ...option,
        disabled: isUsedByOther && option.value !== currentValue,
      };
    });

    // Capitalize first letter of a string
    const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    // Handle custom property selection from FormFieldContactProperty
    const handleCustomPropertySelected = (data: {
      propertyValue: string;
      propertyType: string;
      hasRelationship: boolean;
      options: string[];
    }) => {
      const { propertyType, hasRelationship, options } = data;

      // Hide the component for bool type, show for others
      isVisible.value = propertyType !== 'bool';

      // Type "list" with options
      if (propertyType === 'list' && options.length > 0) {
        confirm.require({
          group: 'autoGenerateOptions',
          message: t('templateBuilder.formOptions.autoGenerateMessage'),
          header: t('templateBuilder.formOptions.autoGenerateTitle'),
          icon: 'pi pi-question-circle',
          acceptLabel: t('templateBuilder.formOptions.autoGenerateYes'),
          rejectLabel: t('templateBuilder.formOptions.autoGenerateNo'),
          accept: () => {
            // Generate options from schema
            clearOptions();
            // Set restrictAddOption BEFORE adding options
            restrictAddOption.value = true;
            // Create optionsList with original values (labels and values both lowercase from schema)
            optionsList.value = options.map((opt) => ({ label: opt, value: opt }));
            // Add options with capitalized labels but original values
            options.forEach((option) => {
              addOption(capitalizeFirstLetter(option), option, false);
            });
            handleOptionChange();
          },
          reject: () => {
            // Create single generic option
            clearOptions();
            addOption('Option 1', 'Option 1', false);
            handleOptionChange();
          },
        });
      } else if (hasRelationship) {
        // Type with relationship to another custom schema
        confirm.require({
          group: 'autoGenerateOptions',
          message: t('templateBuilder.formOptions.relationshipMessage'),
          header: t('templateBuilder.formOptions.relationshipTitle'),
          icon: 'pi pi-question-circle',
          acceptLabel: t('templateBuilder.formOptions.autoGenerateYes'),
          rejectLabel: t('templateBuilder.formOptions.autoGenerateNo'),
          accept: async () => {
            // User accepted to generate options from relationship
            // Load related schema fields and show modal
            await loadRelatedSchemaFields(data.propertyValue);
          },
          reject: () => {
            // User rejected to generate options from relationship
            clearOptions();
            addOption('Option 1', 'Option 1', false);
            handleOptionChange();
          },
        });
      } else if (propertyType === 'bool') {
        // Bool type: no options needed
        clearOptions();
        handleOptionChange();
      } else {
        // Simple type (text, number, etc.) - no modal, just create one option
        clearOptions();
        addOption('Option 1', 'Option 1', false);
        handleOptionChange();
      }
    };

    // Load fields from related custom schema and show modal
    const loadRelatedSchemaFields = async (propertyValue: string) => {
      try {
        // Find the schema that contains this property
        const parentSchema = fullCustomSchemas.value
          .find((schema) => schema.properties.some((prop: any) => prop.value === propertyValue));

        if (!parentSchema) {
          console.error('Parent schema not found for property:', propertyValue);
          clearOptions();
          addOption('Option 1', 'Option 1', false);
          handleOptionChange();
          return;
        }

        // Find the property and its relationship
        const property = parentSchema.properties.find((prop: any) => prop.value === propertyValue);
        if (!property) {
          console.error('Property not found:', propertyValue);
          clearOptions();
          addOption('Option 1', 'Option 1', false);
          handleOptionChange();
          return;
        }

        // Fetch all schemas to find the related one
        const allSchemas = await fetchCustomSchemas();

        // Find relationship for this property
        const relationship = allSchemas
          .find((s: any) => s.id_custom_object?.toString() === parentSchema.schemaId)
          ?.relationships?.find((rel: any) => rel.sourceField === property.name && rel.targetSchemaType === 'custom');

        if (!relationship) {
          console.error('No relationship found for property:', property.name);
          clearOptions();
          addOption('Option 1', 'Option 1', false);
          handleOptionChange();
          return;
        }

        // Find the target schema
        const targetSchema = allSchemas.find(
          (s: any) => s.id_custom_object?.toString() === relationship.targetSchema
            || s.table_name === relationship.targetSchema
            || s.schema_name === relationship.targetSchema,
        );

        if (!targetSchema || !targetSchema.fields) {
          console.error('Target schema not found:', relationship.targetSchema);
          clearOptions();
          addOption('Option 1', 'Option 1', false);
          handleOptionChange();
          return;
        }

        // Filter fields to only text and number types
        const textAndNumberFields = targetSchema.fields.filter(
          (field: any) => field.type === 'text' || field.type === 'number',
        );

        if (textAndNumberFields.length === 0) {
          console.warn('No text or number fields found in target schema');
          clearOptions();
          addOption('Option 1', 'Option 1', false);
          handleOptionChange();
          return;
        }

        // Populate available fields for dropdown
        availableLabelFields.value = textAndNumberFields.map((field: any) => ({
          label: field.label || field.name,
          value: field.name,
        }));

        // Set first field as default selection
        selectedLabelField.value = textAndNumberFields[0].name;

        // Store data for later use
        currentRelationshipData.value = {
          propertyValue,
          relationship,
          targetSchema,
        };

        // Show the modal
        showLabelFieldModal.value = true;
      } catch (error) {
        console.error('Error loading related schema fields:', error);
        clearOptions();
        addOption('Option 1', 'Option 1', false);
        handleOptionChange();
      }
    };

    // Handle user clicking "Yes" in label field modal
    const handleLabelFieldAccept = async () => {
      if (!selectedLabelField.value || !currentRelationshipData.value) {
        showLabelFieldModal.value = false;
        return;
      }

      try {
        const { relationship, targetSchema } = currentRelationshipData.value;

        // Fetch records from the target schema
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/custom-data-records/${targetSchema.id_custom_object}`,
          {},
          '',
          {},
          true,
        );

        // Check if we have data
        if (!response || !response.data || !Array.isArray(response.data)) {
          console.warn('No records found in target schema');
          clearOptions();
          addOption('Option 1', 'Option 1', false);
          handleOptionChange();
          showLabelFieldModal.value = false;
          return;
        }

        const records = response.data;

        if (records.length === 0) {
          console.warn('Target schema has no records');
          clearOptions();
          addOption('Option 1', 'Option 1', false);
          handleOptionChange();
          showLabelFieldModal.value = false;
          return;
        }

        // Generate options from records
        // value = value of the relation field (sourceField)
        // label = value of the selected label field
        clearOptions();

        // Build optionsList for dropdown values (like for list type)
        const generatedOptions: { label: string; value: string }[] = [];

        records.forEach((record: any) => {
          const valueFieldData = record[relationship.targetField] || record[relationship.sourceField];
          const labelFieldData = record[selectedLabelField.value];

          // Only add option if both value and label exist
          if (valueFieldData !== undefined && labelFieldData !== undefined) {
            const valueStr = String(valueFieldData);
            const labelStr = String(labelFieldData);

            addOption(
              labelStr, // label
              valueStr, // value
              false,
            );

            // Add to optionsList for dropdown
            generatedOptions.push({ label: valueStr, value: valueStr });
          }
        });

        // Set optionsList to populate the value dropdown
        optionsList.value = generatedOptions;

        // Enable restriction to prevent adding more options than available records
        restrictAddOption.value = true;

        handleOptionChange();
      } catch (error) {
        console.error('Error fetching custom data records:', error);
        clearOptions();
        addOption('Option 1', 'Option 1', false);
        handleOptionChange();
      }

      showLabelFieldModal.value = false;
    };

    // Handle user clicking "No" in label field modal
    const handleLabelFieldReject = () => {
      clearOptions();
      addOption('Option 1', 'Option 1', false);
      handleOptionChange();
      showLabelFieldModal.value = false;
    };

    const toggleExpanded = (index: number) => {
      optionExpanded.value[index] = !optionExpanded.value[index];
    };

    const sortableInstance = ref();

    const swapElement = (from: number, to: number, array: any[]) => {
      const old = array[from];
      // eslint-disable-next-line no-param-reassign
      array[from] = array[to];
      // eslint-disable-next-line no-param-reassign
      array[to] = old;
      return array;
    };

    const initSortable = () => {
      const formPropertiesWrapper = document.querySelector('#form-properties-wrapper') as HTMLElement;
      if (formPropertiesWrapper) {
        sortableInstance.value = Sortable.create(formPropertiesWrapper, {
          animation: 150,
          handle: '.handle-drag',
          onEnd: (evt) => {
            optionLabels.value = swapElement(evt.oldIndex || 0, evt.newIndex || 0, optionLabels.value);
            optionValues.value = swapElement(evt.oldIndex || 0, evt.newIndex || 0, optionValues.value);
            optionIds.value = swapElement(evt.oldIndex || 0, evt.newIndex || 0, optionIds.value);
            optionExpanded.value = swapElement(evt.oldIndex || 0, evt.newIndex || 0, optionExpanded.value);

            handleOptionChange();
          },
        });
      }
    };

    const updateOptionsList = (init = false) => {
      if (contactProperty.value && contactProperty.value.properties && Array.isArray(contactProperty.value.properties) && contactProperty.value.properties.length) {
        const property = contactProperty.value.properties[0];
        const propertyValue = property.value?.trim();

        // Check if it's a custom property (not native)
        const isNativeProperty = nativeContactProperties.value.find((item) => item.toLowerCase() === propertyValue?.toLowerCase());

        if (propertyValue && !isNativeProperty) {
          // It's a custom property - trigger the new logic only if not init
          if (!init) {
            // For now, we'll create a simple option since we don't have access to full schema here
            // The real logic will be triggered by the event from FormFieldContactProperty
            clearOptions();
            addOption('Option 1', 'Option 1', false);
            handleOptionChange();
            restrictAddOption.value = false;
          }
          return true;
        }

        // If switching back to native property, reset restrictAddOption
        if (restrictAddOption.value && propertyValue && isNativeProperty) {
          clearOptions();
          optionsList.value = [];
          handleOptionChange();
          restrictAddOption.value = false;
          return true;
        }
      }
      return false;
    };

    onMounted(async () => {
      initSortable();
      // Load custom schemas for property analysis
      try {
        fullCustomSchemas.value = await getCustomContactPropertiesWithoutNested();

        // Check if current property is a custom list type and restore restrictAddOption
        const currentPropertyValue = contactProperty.value?.properties?.[0]?.value?.trim();
        const isNativeProperty = nativeContactProperties.value.find((item) => item.toLowerCase() === currentPropertyValue?.toLowerCase());

        if (currentPropertyValue && !isNativeProperty && fullCustomSchemas.value.length > 0) {
          const selectedCustomProperty = fullCustomSchemas.value
            .flatMap((schema) => schema.properties)
            .find((prop) => prop.value === currentPropertyValue);

          if (selectedCustomProperty) {
            // Show/hide component based on property type
            isVisible.value = selectedCustomProperty.type !== 'bool';

            // Restore restrictAddOption for list type
            if (selectedCustomProperty.type === 'list' && selectedCustomProperty.options?.length > 0) {
              const schemaOptions = selectedCustomProperty.options;
              const currentValues = optionValues.value;

              // Check if current options match schema options (ignoring case and order)
              const normalizedCurrentValues = currentValues.map((v: string) => v.toLowerCase()).sort();
              const normalizedSchemaOptions = schemaOptions.map((v: string) => v.toLowerCase()).sort();
              const isMatchingSchema = JSON.stringify(normalizedCurrentValues) === JSON.stringify(normalizedSchemaOptions);

              if (isMatchingSchema) {
                restrictAddOption.value = true;
                optionsList.value = schemaOptions.map((opt: string) => ({ label: opt, value: opt }));
              }
            } else if (selectedCustomProperty.hasRelationship && optionValues.value.length > 0) {
              // Restore restrictAddOption for relationship type
              // If there are existing options, assume they were generated from relationship
              // and enable restriction
              restrictAddOption.value = true;
              optionsList.value = optionValues.value.map((val: string) => ({ label: val, value: val }));
            }
          }
        }
        // Native property with a value selected → show/hide based on type
        if (currentPropertyValue && isNativeProperty) {
          const nativeProp = getNativeContactProperties(t, '').find((item) => item.value.toLowerCase() === currentPropertyValue.toLowerCase());
          isVisible.value = nativeProp?.type !== 'bool';
        }
      } catch (error) {
        console.error('❌ Error loading custom schemas:', error);
      }

      updateOptionsList(true);

      // Enrich variables with collection context
      if (props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          variableList.value,
        ) as never[];
      }
    });

    onBeforeUnmount(() => {
      if (sortableInstance.value) {
        sortableInstance.value.destroy();
      }
    });

    // Store previous property value to detect changes
    const previousPropertyValue = ref<string | undefined>(contactProperty.value?.properties?.[0]?.value);

    watch(() => props.fieldGroup, () => {
      const currentProperty = contactProperty.value;
      const currentPropertyValue = currentProperty?.properties?.[0]?.value;

      // Only trigger if property value actually changed
      if (currentPropertyValue !== previousPropertyValue.value) {
        previousPropertyValue.value = currentPropertyValue;

        const propertyValue = currentPropertyValue?.trim();
        const isNativeProperty = nativeContactProperties.value.find((item) => item.toLowerCase() === propertyValue?.toLowerCase());
        // If it's a custom property and we have the schemas loaded
        if (propertyValue && !isNativeProperty && fullCustomSchemas.value.length > 0) {
          // Find the custom property in schemas
          const selectedCustomProperty = fullCustomSchemas.value
            .flatMap((schema) => schema.properties)
            .find((prop) => prop.value === propertyValue);

          if (selectedCustomProperty) {
            // Call the handler with property details
            handleCustomPropertySelected({
              propertyValue,
              propertyType: selectedCustomProperty.type,
              hasRelationship: selectedCustomProperty.hasRelationship || false,
              options: selectedCustomProperty.options || [],
            });
          } else {
            // Fallback to generic behavior
            updateOptionsList();
          }
        } else {
          // Native property or no schemas loaded - show/hide based on type
          const nativeProp = getNativeContactProperties(t, '').find((item) => item.value.toLowerCase() === propertyValue?.toLowerCase());
          isVisible.value = nativeProp?.type !== 'bool';
          if (nativeProp?.type === 'bool') {
            // Bool type (consent): ensure exactly one checkbox option with the proper label
            clearOptions();
            const elementId = new RegExp('(spm_widget_[0-9a-zA-Z]+)').exec(props.parserValues.selector);
            if (elementId && elementId.length) {
              const optionId = createFormOptionId(elementId[1], '1', props.parserValues.properties[0].type || '');
              optionLabels.value.push(nativeProp.fullLabel || nativeProp.label || 'Option 1');
              optionValues.value.push('');
              optionIds.value.push(optionId);
              optionExpanded.value.push(true);
            }
            handleOptionChange();
            restrictAddOption.value = false;
          } else {
            updateOptionsList();
          }
        }
      }
    }, { deep: true, flush: 'post' });

    return {
      t,
      optionIds,
      optionLabels,
      optionValues,
      enrichedVariables,
      optionsList,
      filterValue,
      optionExpanded,
      restrictAddOption,
      isVisible,
      handleOptionChange,
      handleFilter,
      handleCreateOption,
      addOption,
      deleteOption,
      getAvailableOptions,
      toggleExpanded,
      showLabelFieldModal,
      availableLabelFields,
      selectedLabelField,
      handleLabelFieldAccept,
      handleLabelFieldReject,
    };
  },

});
</script>

<style lang="scss" scoped>
.handle-drag {
  :hover {
    cursor: grab;
  }
}
.form-field-options__wrapper {
  overflow: hidden;
}
.form-field-options__wrapper--collapsed {
  max-height: 0;
}
</style>
